import {EventEmitter, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {Socket} from "ngx-socket-io";
import {SwUpdate} from "@angular/service-worker";
import {PlatformLocation} from "@angular/common";
import {MatSnackBar} from "@angular/material/snack-bar";
import moment from "moment";
import {Platform} from "@angular/cdk/platform";

@Injectable({
  providedIn: 'root'
})
export class UtilitiesService {

  private activeMediaStreams: MediaStream[] = [];
  private db: IDBDatabase | null = null;
  private dbName = 'pwaRunnerDB';
  private dbVersion = 1;
  private objectStoreName = 'dataStore';

  loadingPopup: any;
  loadingLabel: string = 'Sincronizando...';

  online: boolean = true;
  modal_width: number = 0;
  modal_height: number = 0;
  onlineStatus: any;
  offlineStatus: any;
  private drawerControllerEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  public drawerControllerEmitter$ = this.drawerControllerEmitter.asObservable();

  constructor(
    readonly swUpdate: SwUpdate,
    public http: HttpClient,
    public socket: Socket,
    private snackBar: MatSnackBar,
    private location: PlatformLocation,
    private platform: Platform) {

    this.modal_width = window.innerWidth;
    this.modal_height = window.innerHeight;

    this.initIndexedDB().then(() => {
      this.migrateLocalStorageToIndexedDB();
    });
  }

  private initIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve();
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (event) => {
        console.error('Error al abrir IndexedDB:', event);
        reject('Error al abrir IndexedDB');
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.objectStoreName)) {
          db.createObjectStore(this.objectStoreName);
        }
      };
    });
  }

  changeDrawerState(event: boolean) {
    this.drawerControllerEmitter.emit(event);
  }

  /**
   * @param timeStamp timeStamp to compare
   * @param higher if true, returns the time past till the timeStamp, if false, returns the time past since the timeStamp
   * @returns return the seconds past till/since the timeStamp
   */
  public getTimePastTill(timeStamp: string, higher: boolean = true) {
    let client_hour_snapshot = new Date(timeStamp);
    if ((timeStamp).toString().substr((timeStamp).toString().length - 2) == '0Z') {
      client_hour_snapshot.setSeconds(client_hour_snapshot.getMinutes() + client_hour_snapshot.getTimezoneOffset());
    }

    return higher ? Math.floor((+client_hour_snapshot - Date.now()) / 1000)
      : Math.floor((Date.now() - +client_hour_snapshot) / 1000);
  }

  public leadZero(number: number) {
    return (number < 10 ? '0' : '') + number;
  }

  formatoSegundos(sec: any) {
    let date = new Date();
    date.setSeconds(sec);
    return date.toISOString().substr(11, 8);
  }

  /**
   * Guardar objeto en memoria
   * Retrocompatibilidad con localStorage
   * @param key String
   * @param value Object
   * @returns
   */
  async setObject(key: string, value: any) {
    try {
      await this.initIndexedDB();
      return new Promise<void>((resolve, reject) => {
        if (!this.db) {
          window.localStorage.setItem(key, JSON.stringify(value));
          resolve();
          return;
        }

        const transaction = this.db.transaction([this.objectStoreName], 'readwrite');
        const store = transaction.objectStore(this.objectStoreName);
        const request = store.put(value, key);

        request.onsuccess = () => resolve();
        request.onerror = (event) => {
          console.error('Error al guardar en IndexedDB:', event);
          window.localStorage.setItem(key, JSON.stringify(value));
          resolve();
        };
      });
    } catch (error) {
      console.error('Error en setObject:', error);
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  }

  async getObject(key: any) {
    try {
      await this.initIndexedDB();
      return new Promise<string>((resolve, reject) => {
        if (!this.db) {
          const value = window.localStorage.getItem(key) ?? '{"null":true}';
          resolve(value);
          return;
        }

        const transaction = this.db.transaction([this.objectStoreName], 'readonly');
        const store = transaction.objectStore(this.objectStoreName);
        const request = store.get(key);

        request.onsuccess = () => {
          if (request.result !== undefined) {
            resolve(JSON.stringify(request.result));
          } else {
            const value = window.localStorage.getItem(key) ?? '{"null":true}';
            resolve(value);
          }
        };

        request.onerror = (event) => {
          console.error('Error al obtener de IndexedDB:', event);
          const value = window.localStorage.getItem(key) ?? '{"null":true}';
          resolve(value);
        };
      });
    } catch (error) {
      console.error('Error en getObject:', error);
      // Fallback a localStorage
      const value = window.localStorage.getItem(key) ?? '{"null":true}';
      return value;
    }
  }

  public getCurrentTime() {
    return moment().format("YYYY-MM-DD HH:mm:ss");
  }

  public getSecondsDifference(greaterTime: string, lowerTime: string): number {
    return moment(greaterTime).diff(moment(lowerTime), 'seconds');
  }

  notificacion() {
    Notification.requestPermission(function (result) {
      if (result === "granted") {
        navigator.serviceWorker.ready.then(function (registration) {
          registration.showNotification("Notificacion Pickup", {
            body: "Cliente esperando!",
            requireInteraction: true,
            icon: "assets/images/pickup_small.png",
            tag: "vibration-sample",
          });
        });
      }
    });
  }

  patternCheck(element: HTMLInputElement, pattern_type = 'number') {
    let pattern: RegExp | undefined;
    let contrary_pattern: RegExp | undefined;

    switch (pattern_type) {
      case 'number':
        pattern = new RegExp("/[0-9]+$/");
        contrary_pattern = new RegExp("/[^0-9]+$/");
        break;
      case 'string':
        pattern = new RegExp("/[a-zA-Z ]+$/");
        contrary_pattern = new RegExp("/[^a-zA-Z ]+$/");
        break;
      case 'email':
        pattern = new RegExp("/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$/");
        contrary_pattern = new RegExp("/[^a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$/");
        break;
      case 'phone':
        pattern = new RegExp("/[0-9]{3}-[0-9]{3}-[0-9]{4}$/");
        contrary_pattern = new RegExp("/[^0-9]{3}-[0-9]{3}-[0-9]{4}$/");
        break;
      case 'date':
        pattern = new RegExp("/[0-9]{4}\/[0-9]{2}\/[0-9]{2}$/");
        contrary_pattern = new RegExp("/[^0-9]{4}\/[0-9]{2}\/[0-9]{2}$/");
        break;
      case 'rut':
        return this.rutChecker(element);

    }

    if (!pattern?.test(element.value)) element.value = element.value.replace(contrary_pattern ?? '', '');

  }

  stringReducerResume(str: string, length: number) {
    if (!str) str = '';
    return str.length > length ? str.substring(0, length) + "..." : str;
  }

  rutChecker(element: HTMLInputElement) {

    const key = element.value[element.value.length - 1];

    if (element.value.length > 10) return element.value = element.value.substr(0, 10);

    let cleaned_rut = (key.toLowerCase() === 'k' && element.value.length < 8) ?
      element.value.replace(/[^0-9-]/g, "")
      : element.value.replace(/[^0-9kK-]/g, "");

    cleaned_rut = cleaned_rut.replaceAll('-', '');

    cleaned_rut = cleaned_rut.slice(0, -1) + '-' + cleaned_rut.slice(-1);

    element.value = cleaned_rut;

  }

  showSnackBar(message: string, color: string = 'success', duration: number = 10000) {
    this.snackBar.open(message, 'Entendido', {
      duration: duration,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['snackbar', color]
    });
  }

  registerMediaStream(stream: MediaStream): void {
    if (stream) {
      this.activeMediaStreams.push(stream);
      console.log(`Registered new media stream. Total active: ${this.activeMediaStreams.length}`);
    }
  }

  stopAllActiveCameras(): void {
    console.log(`Stopping all active cameras. Count: ${this.activeMediaStreams.length}`);

    this.activeMediaStreams.forEach((stream, index) => {
      try {
        if (stream) {
          const tracks = stream.getTracks();
          tracks.forEach(track => {
            if (track.readyState === 'live') {
              track.stop();
              console.log(`Stopped track: ${track.kind}`);
            }
          });
        }
      } catch (error) {
        console.error(`Error stopping stream at index ${index}:`, error);
      }
    });

    console.log('Entrando a emergency camera cleanup');
    this.activeMediaStreams = [];

    try {
      const allTracks = this.getAllMediaTracks();
      console.log(`Found ${allTracks} additional tracks`);
      allTracks.forEach(track => {
        if (track.readyState === 'live' && track.kind === 'video') {
          console.log('Stopping additional active video track');
          track.stop();
        }
      });
    } catch (error) {
      console.error('Error in fallback camera cleanup:', error);
    }

  }

  private getAllMediaTracks(): MediaStreamTrack[] {
    const tracks: MediaStreamTrack[] = [];

    const videoElements = document.querySelectorAll('video');
    videoElements.forEach(video => {
      try {
        const stream = video.srcObject as MediaStream;
        if (stream) {
          stream.getTracks().forEach(track => {
            tracks.push(track);
          });
        }
      } catch (error) {
        console.error('Error accessing video stream:', error);
      }
    });

    return tracks;
  }

  getCurrentPlatform(): string {
    return this.platform.ANDROID ? 'android' : this.platform.IOS ? 'ios' : 'web';
  }

  /**
   * Asegurar retrocompatibilidad con localStorage
   */
  private async migrateLocalStorageToIndexedDB(): Promise<void> {
    try {
      const knownKeys = ['cola_firma', 'cola_imagen'];

      for (const key of knownKeys) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const parsedValue = JSON.parse(value);
            await this.setObject(key, parsedValue);
            console.log(`Datos migrados de localStorage a IndexedDB: ${key}`);
          } catch (e) {
            console.error(`Error al parsear el valor de ${key} en localStorage:`, e);
          }
        }
      }

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !knownKeys.includes(key)) {
          if (key.startsWith('cola_') || key.includes('pedido') || key.includes('firma') || key.includes('imagen')) {
            try {
              const value = localStorage.getItem(key);
              if (value) {
                const parsedValue = JSON.parse(value);
                await this.setObject(key, parsedValue);
                console.log(`Datos adicionales migrados de localStorage a IndexedDB: ${key}`);
              }
            } catch (e) {
              console.error(`Error al migrar clave adicional ${key}:`, e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error al migrar datos de localStorage a IndexedDB:', error);
    }
  }

  /**
   * Eliminar objeto en memoria
   * Retrocompatibilidad con localStorage (Por si acaso)
   * @param key
   * @returns
   */
  async removeObject(key: string): Promise<void> {
    try {
      await this.initIndexedDB();
      return new Promise<void>((resolve, reject) => {
        if (!this.db) {
          window.localStorage.removeItem(key);
          resolve();
          return;
        }

        const transaction = this.db.transaction([this.objectStoreName], 'readwrite');
        const store = transaction.objectStore(this.objectStoreName);
        const request = store.delete(key);

        request.onsuccess = () => {
          window.localStorage.removeItem(key);
          resolve();
        };

        request.onerror = (event) => {
          console.error('Error al eliminar de IndexedDB:', event);
          window.localStorage.removeItem(key);
          resolve();
        };
      });
    } catch (error) {
      console.error('Error en removeObject:', error);
      window.localStorage.removeItem(key);
    }
  }
}
